import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Container, Text, Sprite, Graphics } from "@pixi/react";
import * as PIXI from "pixi.js";
import { TextStyle, TextMetrics } from "pixi.js";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import anime from "animejs";
import { createButtonPressAnimation } from "../../../../utilities";
import { closeErrorPopup, closeExpirationPopup, setUserInterfaceDisabled, setSpinComplete, setErrorRecoveryTransitionActive, setReelsFrozen } from "../../../../State/actions";
import { selectErrorPopupActive, selectErrorPopupCode, selectReelsFrozen, selectExpirationPopupActive } from "../../../../State/selectors";
import { refreshGameData } from "../../../../State/Slices/gameSettingsSlice";
import { setShouldDeductNextSpin } from "../../../../State/Slices/freeRoundsSlice";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { getUITexture } from "../../../../assets";
import { FONT_CONFIG } from "../../../../textureConfigs";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";

// Development mode
const DEV_MODE = false;

// Overlay settings
const OVERLAY_BACKGROUND_ALPHA = 0.6;
const OVERLAY_BACKGROUND_COLOR = 0x090f41;
const OVERLAY_FADE_DURATION = 200;

// Popup animation settings
const POPUP_ANIM_DURATION = 250;

// Error popup configuration
const ERROR_POPUP_CONFIG = {
  basePadding: 40,
  baseMessageFontSize: 24,
  baseButtonFontSize: 24,
  baseTextButtonGap: 40,
  baseContentWidth: 480,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { scaleMultiplier: 0.55 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.6 },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { scaleMultiplier: 0.75 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.75 },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 1.0 },
    },
  },
};

const WarningPopup = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { dimensions, device, orientation } = useDimensions();
  const errorPopupActive = useSelector(selectErrorPopupActive);
  const errorCode = useSelector(selectErrorPopupCode);
  const reelsFrozen = useSelector(selectReelsFrozen);
  const expirationPopupActive = useSelector(selectExpirationPopupActive);

  const depositUrl = window.__OPTIONS__?.deposit_url;

  const isExpirationMode = !errorPopupActive && expirationPopupActive;
  const isOpen = DEV_MODE ? true : (errorPopupActive || expirationPopupActive);

  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const popupContentRef = useRef(null);

  const okButtonSpriteRef = useRef(null);
  const depositButtonSpriteRef = useRef(null);
  const isOkPressAnimatingRef = useRef(false);
  const isDepositPressAnimatingRef = useRef(false);



  const errorMessage = useMemo(() => {
    if (isExpirationMode) {
      return t("error_session_expired", { ns: "common" });
    }
    switch (errorCode) {
      case 301:
        return t("error_insufficient_funds", { ns: "common" });
      case 400:
        return t("error_invalid_bet", { ns: "common" });
      case 401:
        return t("error_session_invalid", { ns: "common" });
      case 500:
        return t("error_something_wrong", { ns: "common" });
      case 503:
        return t("error_maintenance", { ns: "common" });
      default:
        return t("error_unexpected", { ns: "common" });
    }
  }, [errorCode, isExpirationMode, t]);

  const deviceConfigMemo = useMemo(() => {
    return ERROR_POPUP_CONFIG.DEVICE_CONFIGS[device]?.[orientation] ||
           ERROR_POPUP_CONFIG.DEVICE_CONFIGS[PixiSettings.DEVICE.DESKTOP][PixiSettings.ORIENTATION.LANDSCAPE];
  }, [device, orientation]);

  const centerX = useMemo(() => dimensions.width / 2, [dimensions.width]);
  const centerY = useMemo(() => dimensions.height / 2, [dimensions.height]);

  // Textures
  const buttonTexture = useMemo(() => getUITexture("popup_button"), []);
  const buttonActiveTexture = useMemo(() => getUITexture("popup_button_active"), []);
  const frameTexture = useMemo(() => getUITexture("popup_frame_horizontal"), []);

  // Effective popup scale — clamped so the frame never exceeds 90% of viewport in either dimension.
  // All inner elements (padding, fonts, buttons) derive from this, so everything scales together.
  const popupScale = useMemo(() => {
    const deviceScale = deviceConfigMemo.scaleMultiplier || 1.0;
    if (!frameTexture) return deviceScale;

    const idealContentWidth = ERROR_POPUP_CONFIG.baseContentWidth * deviceScale;
    const idealPadding = ERROR_POPUP_CONFIG.basePadding * deviceScale;
    const idealPanelWidth = idealContentWidth + idealPadding * 2;
    const scaleX = idealPanelWidth / frameTexture.width;
    const idealFrameWidth = frameTexture.width * scaleX;
    const idealFrameHeight = frameTexture.height * scaleX;

    const maxWidth = dimensions.width * 0.8;
    const maxHeight = dimensions.height * 0.8;
    const widthRatio = idealFrameWidth > maxWidth ? maxWidth / idealFrameWidth : 1;
    const heightRatio = idealFrameHeight > maxHeight ? maxHeight / idealFrameHeight : 1;
    const clampRatio = Math.min(widthRatio, heightRatio);

    return deviceScale * clampRatio;
  }, [deviceConfigMemo, frameTexture, dimensions.width, dimensions.height]);

  const padding = useMemo(() => ERROR_POPUP_CONFIG.basePadding * popupScale, [popupScale]);
  const messageFontSize = useMemo(() => Math.round(ERROR_POPUP_CONFIG.baseMessageFontSize * popupScale), [popupScale]);
  const buttonFontSize = useMemo(() => Math.round(ERROR_POPUP_CONFIG.baseButtonFontSize * popupScale), [popupScale]);
  const textButtonGap = useMemo(() => ERROR_POPUP_CONFIG.baseTextButtonGap * popupScale, [popupScale]);
  const contentWidth = useMemo(() => ERROR_POPUP_CONFIG.baseContentWidth * popupScale, [popupScale]);

  // White text, no outline
  const messageStyle = useMemo(() => new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: messageFontSize,
    fontWeight: "700",
    fill: 0xffffff,
    align: "center",
    wordWrap: true,
    wordWrapWidth: contentWidth,
  }), [messageFontSize, contentWidth]);

  const buttonTextStyle = useMemo(() => new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: buttonFontSize,
    fontWeight: "700",
    fill: 0xffffff,
    align: "center",
  }), [buttonFontSize]);

  const textMetrics = useMemo(() => TextMetrics.measureText(errorMessage, messageStyle), [errorMessage, messageStyle]);

  // Button sized proportionally to content width, keeping aspect ratio
  const buttonWidth = useMemo(() => contentWidth * 0.25, [contentWidth]);
  const buttonHeight = useMemo(() => {
    const aspect = buttonTexture.height / buttonTexture.width;
    return buttonWidth * aspect;
  }, [buttonTexture, buttonWidth]);

  const textHeight = useMemo(() => textMetrics.height, [textMetrics.height]);

  // Frame scaling — uniform scale to fit desired panel size while keeping aspect ratio
  const desiredPanelWidth = useMemo(() => contentWidth + padding * 2, [contentWidth, padding]);
  const desiredPanelHeight = useMemo(
    () => padding + textHeight + textButtonGap + buttonHeight + padding,
    [textHeight, textButtonGap, buttonHeight, padding]
  );

  // Scale frame to fit content — use the larger scale to ensure frame covers content
  const frameScale = useMemo(() => {
    if (!frameTexture) return 1;
    const scaleX = desiredPanelWidth / frameTexture.width;
    const scaleY = desiredPanelHeight / frameTexture.height;
    return Math.max(scaleX, scaleY);
  }, [frameTexture, desiredPanelWidth, desiredPanelHeight]);

  const frameSpriteWidth = useMemo(() => frameTexture ? frameTexture.width * frameScale : 0, [frameTexture, frameScale]);
  const frameSpriteHeight = useMemo(() => frameTexture ? frameTexture.height * frameScale : 0, [frameTexture, frameScale]);

  // Button anchored to bottom with padding
  const buttonYPos = useMemo(
    () => frameSpriteHeight / 2 - padding - buttonHeight / 2,
    [frameSpriteHeight, padding, buttonHeight]
  );

  // Text centered in space between top padding and button
  const textAreaTop = useMemo(() => -frameSpriteHeight / 2 + padding, [frameSpriteHeight, padding]);
  const textAreaBottom = useMemo(() => buttonYPos - textButtonGap, [buttonYPos, textButtonGap]);
  const messageY = useMemo(
    () => (textAreaTop + textAreaBottom) / 2,
    [textAreaTop, textAreaBottom]
  );

  const okText = t("ok", { ns: "common" });
  const depositText = t("deposit", { ns: "common" });

  const okButtonX = useMemo(() => -buttonWidth / 2 - padding * 0.5, [buttonWidth, padding]);
  const depositButtonXPos = useMemo(() => buttonWidth / 2 + padding * 0.5, [buttonWidth, padding]);

  const drawOverlay = useCallback((g) => {
    g.clear();
    g.beginFill(OVERLAY_BACKGROUND_COLOR);
    g.drawRect(0, 0, dimensions.width, dimensions.height);
    g.endFill();
  }, [dimensions.width, dimensions.height]);

  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
  }, [isClosing]);

  const handleOkPointerOver = useCallback(() => {
    if (okButtonSpriteRef.current && buttonActiveTexture) {
      okButtonSpriteRef.current.texture = buttonActiveTexture;
    }
  }, [buttonActiveTexture]);

  const handleOkPointerOut = useCallback(() => {
    if (okButtonSpriteRef.current && buttonTexture) {
      okButtonSpriteRef.current.texture = buttonTexture;
    }
  }, [buttonTexture]);

  const handleDepositPointerOver = useCallback(() => {
    if (depositButtonSpriteRef.current && buttonActiveTexture) {
      depositButtonSpriteRef.current.texture = buttonActiveTexture;
    }
  }, [buttonActiveTexture]);

  const handleDepositPointerOut = useCallback(() => {
    if (depositButtonSpriteRef.current && buttonTexture) {
      depositButtonSpriteRef.current.texture = buttonTexture;
    }
  }, [buttonTexture]);

  const handleOkClick = useCallback((e) => {
    e.stopPropagation();
    soundManager.play(SOUND_NAME.button_click);
    if (okButtonSpriteRef.current && !isOkPressAnimatingRef.current) {
      isOkPressAnimatingRef.current = true;
      createButtonPressAnimation(okButtonSpriteRef.current, {
        targetWidth: buttonWidth,
        targetHeight: buttonHeight,
        onComplete: () => {
          isOkPressAnimatingRef.current = false;
          handleClose();
        }
      });
    } else {
      handleClose();
    }
  }, [buttonWidth, buttonHeight, handleClose]);

  const handleDepositClick = useCallback((e) => {
    e.stopPropagation();
    soundManager.play(SOUND_NAME.button_click);
    if (depositButtonSpriteRef.current && !isDepositPressAnimatingRef.current) {
      isDepositPressAnimatingRef.current = true;
      createButtonPressAnimation(depositButtonSpriteRef.current, {
        targetWidth: buttonWidth,
        targetHeight: buttonHeight,
        onComplete: () => {
          isDepositPressAnimatingRef.current = false;
          if (depositUrl) {
            window.location.href = depositUrl;
          }
        }
      });
    } else {
      if (depositUrl) {
        window.location.href = depositUrl;
      }
    }
  }, [buttonWidth, buttonHeight, depositUrl]);

  useEffect(() => {
    if (isOpen && !isClosing) {
      setIsVisible(true);
      dispatch(setUserInterfaceDisabled(true));
      // 1. Overlay fades in first
      anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          // 2. Then popup fades in after overlay is done
          if (popupContentRef.current) {
            popupContentRef.current.alpha = 0;
            popupContentRef.current.scale.set(0.85);
            anime({
              targets: popupContentRef.current,
              alpha: 1,
              duration: POPUP_ANIM_DURATION,
              easing: "easeOutQuad",
              complete: () => {
                setHasAnimated(true);
              },
            });
            anime({
              targets: popupContentRef.current.scale,
              x: 1,
              y: 1,
              duration: POPUP_ANIM_DURATION,
              easing: "easeOutQuad",
            });
          }
        },
      });
    } else if (!isOpen && !isClosing) {
      dispatch(setUserInterfaceDisabled(false));
      setIsVisible(false);
      setOverlayAlpha(0);
      setHasAnimated(false);
    }
  }, [isOpen, isClosing, dispatch]);

  useEffect(() => {
    if (!isClosing) return;
    let overlayAnim = null;
    let popupAnim = null;
    if (popupContentRef.current) {
      // 1. Popup fades out first
      popupAnim = anime({
        targets: popupContentRef.current,
        alpha: 0,
        duration: POPUP_ANIM_DURATION,
        easing: "easeInQuad",
      });
      anime({
        targets: popupContentRef.current.scale,
        x: 0.85,
        y: 0.85,
        duration: POPUP_ANIM_DURATION,
        easing: "easeInQuad",
      });
    }
    // 2. Then overlay fades out after popup is done
    overlayAnim = anime({
      targets: { alpha: OVERLAY_BACKGROUND_ALPHA },
      alpha: 0,
      duration: OVERLAY_FADE_DURATION,
      delay: POPUP_ANIM_DURATION,
      easing: "easeInQuad",
      update: (anim) => {
        setOverlayAlpha(anim.animations[0].currentValue);
      },
      complete: () => {
        setOverlayAlpha(0);
        dispatch(setUserInterfaceDisabled(false));
        if (isExpirationMode) {
          dispatch(closeExpirationPopup());
          window.location.reload();
        } else {
          dispatch(closeErrorPopup());
          dispatch(setSpinComplete(true));
          if (reelsFrozen) {
            dispatch(setErrorRecoveryTransitionActive(true));
            dispatch(setReelsFrozen(false));
          }
          if (errorCode === 205) {
            dispatch(refreshGameData({
              useApiData: reelsFrozen,
              ignoreFreeRoundsStorage: true,
            }));
          }
          if (errorCode === 206) {
            dispatch(setShouldDeductNextSpin(true));
          }
        }
        setIsClosing(false);
        setIsVisible(false);
        setHasAnimated(false);
      },
    });
    return () => {
      if (overlayAnim) overlayAnim.pause();
      if (popupAnim) popupAnim.pause();
    };
  }, [isClosing, dispatch, reelsFrozen, errorCode, isExpirationMode]);

  useEffect(() => {
    return () => {
    };
  }, []);

  if (!isVisible && !isOpen) {
    return null;
  }

  return (
    <Container name="warning_popup" zIndex={10000} sortableChildren={true}>
      <Graphics
        draw={drawOverlay}
        alpha={overlayAlpha}
        eventMode="static"
        pointerdown={(e) => { e.stopPropagation(); }}
        zIndex={10000}
      />

      <Container
        name="popup_content"
        ref={popupContentRef}
        zIndex={10001}
        x={centerX}
        y={centerY}
        alpha={hasAnimated ? 1 : 0}
        scale={hasAnimated ? 1 : 0.85}
      >
        {/* Frame image background — anchor 0.5 centers it at (0,0) */}
        {frameTexture && (
          <Sprite
            texture={frameTexture}
            anchor={0.5}
            width={frameSpriteWidth}
            height={frameSpriteHeight}
          />
        )}

        {/* Error message */}
        <Text
          text={errorMessage}
          style={messageStyle}
          anchor={0.5}
          x={0}
          y={messageY}
          resolution={2}
        />

        {/* Buttons */}
        {errorCode === 301 && depositUrl ? (
          <>
            <Container name="ok_button" x={okButtonX} y={buttonYPos}>
              <Graphics
                name="ok_button_interactive_area"
                eventMode="static"
                cursor="pointer"
                hitArea={new PIXI.Rectangle(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight)}
                onpointertap={handleOkClick}
                onpointerover={handleOkPointerOver}
                onpointerout={handleOkPointerOut}
                draw={(g) => {
                  g.clear();
                  g.beginFill(0x000000, 0.01);
                  g.drawRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight);
                  g.endFill();
                }}
                accessible={true}
                accessibleTitle={okText}
              />
              <Sprite ref={okButtonSpriteRef} texture={buttonTexture} anchor={0.5} width={buttonWidth} height={buttonHeight} eventMode="none" />
              <Text text={okText} style={buttonTextStyle} anchor={0.5} resolution={2} eventMode="none" />
            </Container>

            <Container name="deposit_button" x={depositButtonXPos} y={buttonYPos}>
              <Graphics
                name="deposit_button_interactive_area"
                eventMode="static"
                cursor="pointer"
                hitArea={new PIXI.Rectangle(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight)}
                onpointertap={handleDepositClick}
                onpointerover={handleDepositPointerOver}
                onpointerout={handleDepositPointerOut}
                draw={(g) => {
                  g.clear();
                  g.beginFill(0x000000, 0.01);
                  g.drawRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight);
                  g.endFill();
                }}
                accessible={true}
                accessibleTitle={depositText}
              />
              <Sprite ref={depositButtonSpriteRef} texture={buttonTexture} anchor={0.5} width={buttonWidth} height={buttonHeight} eventMode="none" />
              <Text text={depositText} style={buttonTextStyle} anchor={0.5} resolution={2} eventMode="none" />
            </Container>
          </>
        ) : (
          <Container name="ok_button" x={0} y={buttonYPos}>
            <Graphics
              name="ok_button_interactive_area"
              eventMode="static"
              cursor="pointer"
              hitArea={new PIXI.Rectangle(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight)}
              onpointertap={handleOkClick}
              onpointerover={handleOkPointerOver}
              onpointerout={handleOkPointerOut}
              draw={(g) => {
                g.clear();
                g.beginFill(0x000000, 0.01);
                g.drawRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight);
                g.endFill();
              }}
              accessible={true}
              accessibleTitle={okText}
            />
            <Sprite ref={okButtonSpriteRef} texture={buttonTexture} anchor={0.5} width={buttonWidth} height={buttonHeight} eventMode="none" />
            <Text text={okText} style={buttonTextStyle} anchor={0.5} resolution={2} eventMode="none" />
          </Container>
        )}
      </Container>
    </Container>
  );
};

export default WarningPopup;
